// Port of ios/Sources/ViewModels/GroupMessageConversionForm.swift.
//
// The pure decisions behind converting a group message into a private channel
// (shared/API.md §32): permission filtering, initial team selection and validation.
//
// A group message has no team, while the resulting private channel must belong to one. The
// server returns only teams shared by every participant; the locally cached role grants then
// narrow that answer to teams where the reader can actually create a private channel. Keeping
// those two gates separate prevents a form that can only end in 403. Kotlin twin:
// `GroupMessageConversionForm.kt`.
import { CreateChannelForm } from './createChannelForm';

export type Failure = 'nameTaken' | 'notPermitted' | 'other';

export interface TeamChoice {
  id: string;
  displayName: string;
}

export function canCreatePrivateChannel(permissions: ReadonlySet<string>): boolean {
  return permissions.has('manage_system') || permissions.has('create_private_channel');
}

export function eligibleTeams<T extends TeamChoice>(commonTeams: readonly T[], permissionsByTeam: ReadonlyMap<string, ReadonlySet<string>>): T[] {
  return commonTeams
    .filter((t) => canCreatePrivateChannel(permissionsByTeam.get(t.id) ?? new Set()))
    .slice()
    .sort((a, b) => {
      const x = a.displayName.toLowerCase();
      const y = b.displayName.toLowerCase();
      return x < y ? -1 : x > y ? 1 : 0;
    });
}

export function initialTeamId(teams: readonly TeamChoice[], preferredTeamId: string | null | undefined): string | null {
  return teams.find((t) => t.id === preferredTeamId)?.id ?? teams[0]?.id ?? null;
}

export function initialDisplayName(groupTitle: string): string {
  return [...groupTitle.trim()].slice(0, CreateChannelForm.maxDisplayName).join('');
}

export function isSubmittable(teamId: string | null | undefined, displayName: string): boolean {
  return teamId != null && CreateChannelForm.isSubmittable(displayName);
}

export function failure(statusCode: number | null | undefined): Failure {
  switch (statusCode) {
    case 400:
      return 'nameTaken';
    case 403:
      return 'notPermitted';
    default:
      return 'other';
  }
}

export const GroupMessageConversionForm = {
  canCreatePrivateChannel,
  eligibleTeams,
  initialTeamId,
  initialDisplayName,
  isSubmittable,
  failure,
} as const;
